<script setup lang="ts">
import { useDialog } from 'primevue/usedialog'
import { storeToRefs } from 'pinia'
import Debug from 'debug'
import { RepeatOrderStatus } from '~/lib/models/repeat-order'
import { ReceiveOrderError, StoreOrder } from '~/lib/services/store/order/order.dto'
import { ToastType } from '~/lib/models/ui/toast'
import { isPreviousToPayment } from '~/utils/orderUtils'

const authStore = useAuthStore()
const appStore = useAppStore()
const debug = Debug('app:component:InProgressOrder')
const { $toast } = useNuxtApp()

const { navigateToHome, checkoutRoute, cartRoute } = useMcdRoutes()
const { cancelOrder, repeatOrder, receiveOrder, goRateOrder } = useOrderActions()
const {
  trackRepeatOrder,
  trackRepeatOrderOK,
  trackGoToPay,
  trackClickGetPreOrder,
  trackClickArrived,
  trackBeginCheckout,
} = useGTM()
const { t } = useLocale()
const dialog = useDialog()
const { orderServiceRef } = useStoreService()
const orderService = orderServiceRef()

const fetching = ref(false)
const loader = ref(true)
const { pickupMethodRefresh } = storeToRefs(appStore)
const receiveStatus = ref<{ status?: ReceiveOrderError; order: StoreOrder }>()

const { data: pendingOrders, refresh } = await useWrapperAsyncData(() => {
  loader.value = true
  const order = orderService.getPendingOrdersByUser({
    customerToken: authStore.token!,
    countryCode: appStore.countryCode,
  })
  loader.value = false
  return order
})

watch(pickupMethodRefresh, async pickupMethodValue => {
  if (pickupMethodValue) {
    appStore.pickupMethodRefresh = false
    await refresh()
  }
})

const onCancelOrder = async (order: StoreOrder) => {
  const result = await showDialogAlert({
    dialog,
    data: {
      title: t('shoppingCart.alerts.cancelOrderConfirmation.title'),
      body: t('shoppingCart.alerts.cancelOrderConfirmation.body'),
      btnSecondary: t('shoppingCart.alerts.cancelOrderConfirmation.cancel'),
      btnPrimary: t('shoppingCart.alerts.cancelOrderConfirmation.accept'),
    },
  })

  fetching.value = true

  if (result)
    await cancelOrder(
      { id: order.id, isPaymentPending: isPreviousToPayment(order) },
      async () => await refresh(),
      (key: string, error: string) => {
        debug('Error on cancel order ', key, error)
        // toast error
        $toast.show({
          content: `${t('orders.toast.orderCanceledError')}. ${t(
            `orders.toast.orderCanceled${order.type}`
          )}`,
          type: ToastType.ERROR,
        })
      }
    )
  fetching.value = false
}

const payOrder = async (order: StoreOrder) => {
  // go_to_pay
  trackGoToPay()
  appStore.setCurrentOrder(order)
  // here begin_checkout
  trackBeginCheckout({ order, cartProducts: undefined })
  // go to checkout
  return await navigateTo(checkoutRoute())
}

const arrivalRestaurant = (order: StoreOrder) => {
  trackClickArrived()
  appStore.setCurrentOrder(order)
  appStore.pickupMethodModal = true
}

const repeatOrderAction = async (order: StoreOrder) => {
  if (!pendingOrders.value) return
  trackRepeatOrder(order.id)
  fetching.value = true
  const { status, response } = await repeatOrder(order.id)

  const result = await showDialogAlert({
    dialog,
    data: {
      title: t(`repeatOrder.${status}.title`),
      body: t(`repeatOrder.${status}.body`, { pickup: t('common.MOP') }),
      btnPrimary: t(`repeatOrder.${status}.accept`),
      btnSecondary: t(`repeatOrder.${status}.cancel`),
    },
  })

  if (status !== RepeatOrderStatus.SUCCESS) {
    switch (status) {
      case 'mopNotAvailable':
        if (result) await navigateToHome()
        break
      case 'dlvNotAvailable':
        if (result) await navigateToHome()
        break
      case 'genericError':
        if (result) await repeatOrderAction(order)
        else await navigateToHome()
        break
    }
  } else if (response && result) {
    trackRepeatOrderOK(order.id)
    const success = await appStore.repeatOrder(response)
    if (success) return await navigateTo(cartRoute())
  }

  fetching.value = false
}

const doReceiveOrder = async (order: StoreOrder) => {
  trackClickGetPreOrder()
  receiveStatus.value = {
    order,
  }
  const { shouldContinue, receiveOrderError } = await receiveOrder(
    dialog,
    t,
    buildFormattedAddress(order.deliveryPlace, order.area),
    order.id
  )
  if (shouldContinue) {
    // refetch pendings list
    await refresh()
  } else if (receiveOrderError && receiveStatus.value?.order) {
    receiveStatus.value = {
      status: receiveOrderError,
      order: receiveStatus.value.order,
    }
  }
}
</script>
<template>
  <template v-if="pendingOrders">
    <section
      v-if="pendingOrders.length > 0"
      class="mcd-custom-scrollbar p-4 md:h-[50vh] md:overflow-y-scroll"
    >
      <OrderCard
        v-for="order in pendingOrders"
        :key="order.id"
        :order="order"
        :fetching="fetching"
        @pay-order="payOrder(order)"
        @cancel-order="onCancelOrder(order)"
        @arrival-restaurant="arrivalRestaurant(order)"
        @repeat-order="repeatOrderAction(order)"
        @rate-order="goRateOrder(order, 'smartcard')"
        @receive-order="doReceiveOrder(order)"
      />
    </section>

    <figure v-else class="flex flex-col items-center justify-center">
      <img class="h-60 w-60" src="/images/icons/empty-bag.svg" alt="Orders Empty" />
      <h3 class="text-sm">{{ t('orders.historyOrders.onGoing') }}</h3>
    </figure>

    <LazyReceiveOrderError
      v-if="receiveStatus?.status"
      :receive-order-status="receiveStatus"
      @close="receiveStatus = undefined"
    />
  </template>
</template>
